import { baseApi } from './baseApi';
import type {
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  AuthResponse,
  VerifyOtpRequest,
  ResendOtpRequest,
  GoogleAuthRequest,
  GoogleAuthResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  User,
  Wallet,
} from '@nimbazaar/types';
import { API_ENDPOINTS } from '@nimbazaar/constants';

// Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Register - sends OTP email
    register: builder.mutation<ApiResponse<RegisterResponse>, RegisterRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.REGISTER,
        method: 'POST',
        body: data,
      }),
    }),

    // Verify OTP
    verifyOtp: builder.mutation<ApiResponse<AuthResponse>, VerifyOtpRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.VERIFY_OTP,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Resend OTP
    resendOtp: builder.mutation<ApiResponse<{ message: string }>, ResendOtpRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.RESEND_OTP,
        method: 'POST',
        body: data,
      }),
    }),

    // Login
    login: builder.mutation<ApiResponse<AuthResponse>, LoginRequest>({
      query: (credentials) => ({
        url: API_ENDPOINTS.AUTH.LOGIN,
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User'],
    }),

    // Logout
    logout: builder.mutation<void, void>({
      query: () => ({
        url: API_ENDPOINTS.AUTH.LOGOUT,
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Cart'],
    }),

    // Get current user
    getMe: builder.query<ApiResponse<{ user: User; wallet: Wallet; profile?: unknown }>, void>({
      query: () => API_ENDPOINTS.AUTH.ME,
      providesTags: ['User'],
    }),

    // Refresh token
    refreshToken: builder.mutation<ApiResponse<{ accessToken: string; refreshToken: string; expiresIn: number }>, { refreshToken: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.REFRESH,
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot password
    forgotPassword: builder.mutation<ApiResponse<{ message: string }>, { email: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        method: 'POST',
        body: data,
      }),
    }),

    // Verify reset OTP
    verifyResetOtp: builder.mutation<ApiResponse<{ resetToken: string }>, { email: string; otp: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.VERIFY_RESET_OTP,
        method: 'POST',
        body: data,
      }),
    }),

    // Reset password
    resetPassword: builder.mutation<ApiResponse<{ message: string }>, { token: string; password: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.RESET_PASSWORD,
        method: 'POST',
        body: data,
      }),
    }),

    // Google OAuth - authenticate with Google ID token
    googleAuth: builder.mutation<ApiResponse<GoogleAuthResponse>, GoogleAuthRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.GOOGLE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Link Google account to existing user
    linkGoogle: builder.mutation<ApiResponse<{ user: User }>, GoogleAuthRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.AUTH.GOOGLE_LINK,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Unlink Google account
    unlinkGoogle: builder.mutation<ApiResponse<{ message: string }>, void>({
      query: () => ({
        url: API_ENDPOINTS.AUTH.GOOGLE_UNLINK,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),

    // Update user profile
    updateProfile: builder.mutation<ApiResponse<UpdateProfileResponse>, UpdateProfileRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.USERS.UPDATE_PROFILE,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Update user role
    updateRole: builder.mutation<ApiResponse<{ user: User }>, { role: 'BUYER' | 'SELLER' | 'BOTH' }>({
      query: (data) => ({
        url: API_ENDPOINTS.USERS.ME,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Get presigned URL for file upload
    getPresignedUrl: builder.mutation<
      ApiResponse<{ uploadUrl: string; key: string; publicUrl: string }>,
      { filename: string; contentType: string; folder: 'ads' | 'bids' | 'avatars' | 'categories' | 'chat'; size?: number }
    >({
      query: (data) => ({
        url: API_ENDPOINTS.UPLOAD.PRESIGNED_URL,
        method: 'POST',
        body: data,
      }),
    }),

    // Get batch presigned URLs for multiple files
    getPresignedUrls: builder.mutation<
      ApiResponse<{ uploadUrl: string; key: string; publicUrl: string }[]>,
      { files: { filename: string; contentType: string; size?: number }[]; folder: 'ads' | 'bids' | 'avatars' | 'categories' | 'chat' }
    >({
      query: (data) => ({
        url: API_ENDPOINTS.UPLOAD.PRESIGNED_URLS,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useLoginMutation,
  useLogoutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useRefreshTokenMutation,
  useForgotPasswordMutation,
  useVerifyResetOtpMutation,
  useResetPasswordMutation,
  useGoogleAuthMutation,
  useLinkGoogleMutation,
  useUnlinkGoogleMutation,
  useUpdateProfileMutation,
  useUpdateRoleMutation,
  useGetPresignedUrlMutation,
  useGetPresignedUrlsMutation,
} = authApi;
